import * as React from 'react';

import {
  AUTHENTICATION_CODE_ENDPOINT,
  AUTH_TOKEN_NAME,
  OAUTH_CLIENT_ID,
  ADMIN_APP_URL,
  SIGNIN_WITH_ITDB,
  SYSTEM_UNION_CODE,
  API_URL
} from '../../config';
import itdbImage from '../../assets/images/ITDB_graph_logo_small.png';
import { useEffect, useState } from 'react';
import axios from "axios";
import LoadingSpinner from "../widgets/LoadingSpinner";
import {fetchOpenIdConfiguration, buildAuthorizationUrlWithPkce} from "../../utilities/AuthHelper";

const hasSessionStorage = () => typeof window !== 'undefined' && window.sessionStorage;

const clearPkceContext = (country) => {
  if (!hasSessionStorage()) return;
  sessionStorage.removeItem('pkce:context');
  sessionStorage.removeItem('oauth2:context');
  sessionStorage.removeItem('sso:context');
  if (country) {
    sessionStorage.removeItem(`pkce:${country}:code_verifier`);
    sessionStorage.removeItem(`pkce:${country}:state`);
  }
};

const persistPkceContext = ({ country, issuerUri, clientId, scopes, redirectUri, codeVerifier, state }) => {
  if (!hasSessionStorage()) return;
  const keyPrefix = `pkce:${country}`;
  sessionStorage.setItem('pkce:context', JSON.stringify({ country, issuerUri, clientId, scopes, redirectUri, method: 'OIDC_PKCE' }));
  sessionStorage.setItem(`${keyPrefix}:code_verifier`, codeVerifier);
  sessionStorage.setItem(`${keyPrefix}:state`, state);
  sessionStorage.setItem('sso:context', JSON.stringify({ method: 'OIDC_PKCE', country }));
};

const persistSamlContext = ({ country }) => {
  if (!hasSessionStorage()) return;
  sessionStorage.setItem('sso:context', JSON.stringify({ method: 'SAML', country }));
};

const persistOauth2Context = ({ country, tokenEndpoint, clientId, redirectUri }) => {
  if (!hasSessionStorage()) return;
  sessionStorage.setItem(
    'oauth2:context',
    JSON.stringify({ country, tokenEndpoint, clientId, redirectUri, method: 'OIDC_SECRET' })
  );
  sessionStorage.setItem('sso:context', JSON.stringify({ method: 'OIDC_SECRET', country }));
};

const buildOAuth2LoginUrl = ({ authorizationEndpoint, clientId, redirectUri, scopes }) => {
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: scopes.join(' '),
    prompt: 'login'
  });
  const separator = authorizationEndpoint.includes('?') ? '&' : '?';
  return authorizationEndpoint + separator + params.toString();
};

export const SigninButton = ( { countryCode }) => {

  const useITDBForLogin = SIGNIN_WITH_ITDB === '1';
  const unionModeEnabled = Boolean(SYSTEM_UNION_CODE);

  const [, setSelectedCountry] = useState(countryCode ? countryCode.toUpperCase() : '');
  const [, setPendingCountry] = useState(countryCode ? countryCode.toUpperCase() : '');
  const [ssoError, setSsoError] = useState('');
  const [loadingSso, setLoadingSso] = useState(true);
  const [idpSettings, setIdpSettings] = useState(null);
  const [ssoLoginUrl, setSsoLoginUrl] = useState(null);

  useEffect(() => {
    const normalised = countryCode ? countryCode.toUpperCase() : '';
    setSelectedCountry(normalised);
    setPendingCountry(normalised);

    const load = async () => {
      if (!normalised) {
        clearPkceContext();
        setIdpSettings(null);
        setSsoError('');
        setLoadingSso(false);
        setSsoLoginUrl(null);
        return;
      }

      try {
        setLoadingSso(true);
        const { data } = await axios.get(API_URL + '/idpSettings/' + normalised.toLowerCase());
        setIdpSettings(data);
        setSsoError('');

        const country = (data?.countryCode || normalised || '').toLowerCase();
        clearPkceContext(country);

        if (!data?.enabled) {
          setSsoLoginUrl(null);
          setSsoError('SSO currently unavailable');
          return;
        }

        const idpType = data?.idpType;

        if (idpType === 'SAML') {
          if (data?.samlMetadataUrl || data?.samlMetadataXml) {
            const loginUrl = `${API_URL}/saml2/authenticate/${country}?redir=${encodeURIComponent(ADMIN_APP_URL + '/auth/OauthCallback')}`;
            persistSamlContext({ country });
            setSsoLoginUrl(loginUrl);
            setSsoError('');
          } else {
            console.log('[SAML] Missing metadata for country', country);
            setSsoLoginUrl(null);
            setSsoError('Required SAML metadata missing for SSO');
          }
          return;
        }

        if (idpType === 'OIDC_PKCE' && data?.oauth2IssuerUri && data?.oauth2ClientId) {
          // 1) Discover authorization endpoint
          const cfg = await fetchOpenIdConfiguration(data.oauth2IssuerUri);

          // 2) Build PKCE login URL
          const scopes = (data.oauth2ScopesCsv || 'openid')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);

          const { loginUrl, codeVerifier, state } = await buildAuthorizationUrlWithPkce({
            authorizationEndpoint: cfg.authorization_endpoint,
            clientId: data.oauth2ClientId,
            redirectUri: ADMIN_APP_URL + '/auth/openid/callback', // from your config
            scopes,
            // Put any extras you may need here:
            extraParams: { prompt : 'login' }
          });

          persistPkceContext({
            country,
            issuerUri: data.oauth2IssuerUri,
            clientId: data.oauth2ClientId,
            scopes,
            redirectUri: ADMIN_APP_URL + '/auth/openid/callback',
            codeVerifier,
            state
          });

          // 4) Provide ready-made URL to the button
          setSsoLoginUrl(loginUrl);
          return;
        }

        if (idpType === 'OIDC_SECRET' && data?.oauth2IssuerUri && data?.oauth2ClientId) {
          const cfg = await fetchOpenIdConfiguration(data.oauth2IssuerUri);
          if (!cfg?.authorization_endpoint || !cfg?.token_endpoint) {
            console.warn('[OIDC_SECRET] Missing endpoints from issuer discovery', {
              authorizationEndpoint: cfg?.authorization_endpoint,
              tokenEndpoint: cfg?.token_endpoint
            });
            setSsoLoginUrl(null);
            setSsoError('SSO currently unavailable');
            return;
          }

          const scopes = (data.oauth2ScopesCsv || 'openid')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);

          const loginUrl = buildOAuth2LoginUrl({
            authorizationEndpoint: cfg.authorization_endpoint,
            clientId: data.oauth2ClientId,
            redirectUri: ADMIN_APP_URL + '/auth/OauthCallback',
            scopes
          });

          persistOauth2Context({
            country,
            tokenEndpoint: cfg.token_endpoint,
            clientId: data.oauth2ClientId,
            redirectUri: ADMIN_APP_URL + '/auth/OauthCallback'
          });

          setSsoLoginUrl(loginUrl);
          setSsoError('');
          return;
        }

        setSsoLoginUrl(null);
        setSsoError('Unsupported SSO configuration');
      } catch (e) {
        console.log('Error loading identity provider / discovery for ' + normalised, e);
        setIdpSettings(null);
        setSsoLoginUrl(null);
        setSsoError('SSO currently unavailable');
      } finally {
        setLoadingSso(false);
      }
    };

    load();
  }, [countryCode]);

  const getUrl = () => {
    return AUTHENTICATION_CODE_ENDPOINT +
      "?client_id=" + OAUTH_CLIENT_ID +
      "&response_type=code" +
      "&redirect_uri=" + ADMIN_APP_URL + '/auth/OauthCallback' +
      "&scope=openid"
  };

  const handleLogin = () => {       
    localStorage.removeItem(AUTH_TOKEN_NAME);
    window.location.href = getUrl()
  }

  const handleSSOLogin = () => {
    if (!ssoLoginUrl)
      return;
    localStorage.removeItem(AUTH_TOKEN_NAME);
    window.location.href = ssoLoginUrl
  }

  const itdbButton =
    <div className="d-grid gap-2 px-lg-5">
      {useITDBForLogin ? <button type="button" name="btnSignInWithITDB"
                                 className="btn btn-outline-primary btn-lg" onClick={handleLogin}>
        <img src={itdbImage} width="30" alt='Sign in with ITDB' className='mx-2' />
        Sign in with ITDB
      </button>
        :
        <button className="btn btn-primary" onClick={handleLogin}>Sign in</button>}
    </div>

  let ssoButton = null;

  if (unionModeEnabled) {
    if (loadingSso)
      ssoButton = <LoadingSpinner/>
    else if (idpSettings || ssoError) {
      ssoButton = <div className="d-grid gap-2 px-lg-5">
        <button type="button" name="btnSignInWithSSO"
                className="btn btn-outline-primary btn-lg"
                disabled={!ssoLoginUrl}
                onClick={handleSSOLogin}>
          <i className="fa fa-user-shield mx-2" aria-hidden="true"></i>
          Sign in with SSO
        </button>
        {ssoError && <div className="text-danger small text-center">{ssoError}</div>}
      </div>
    }
  }

  return <div className="d-grid gap-2 px-lg-5">
    {itdbButton}
    {ssoButton}
  </div>
};

